import { CoreTypes, type ObjectRecord } from '@vp/shared-types';
import type { Editor } from '@vp/render';
import { moduleSearchText } from '../modules';

/**
 * Text of the board for "Find on canvas" (Ctrl+F): what each object shows — its text,
 * frame titles, scheme captions, text drawn by modules — plus its name. A module may give the exact
 * text of its object (SDK registerSearchText); otherwise its drawing (text primitives) is searched.
 * Colors, ids and other data are not text and are left out.
 */
const cache = new Map<string, { key: string; text: string }>();

const COLORISH = /^#[0-9a-f]{3,8}$|^rgba?\(|^[0-9A-HJKMNP-TV-Z]{26}$/i;

function propsText(v: unknown, out: string[], depth = 0): void {
  if (depth > 8 || out.length > 500) return;
  if (typeof v === 'string') {
    if (v.trim() && !COLORISH.test(v.trim())) out.push(v);
  } else if (Array.isArray(v)) for (const x of v) propsText(x, out, depth + 1);
  else if (v && typeof v === 'object') for (const x of Object.values(v)) propsText(x, out, depth + 1);
}

/** Lowercase text of an object with whitespace collapsed; `pending` while a module is still asked. */
export function objectText(ed: Editor, o: ObjectRecord): { text: string; pending: boolean } {
  const rev = ed.doc.rev(o.id);
  const isModule = !o.type.startsWith('core:');
  const fromModule = isModule ? moduleSearchText(o, rev) : null;
  const key = rev + ':' + (typeof fromModule === 'string' ? 'm' + fromModule.length : fromModule === undefined ? 'p' : 'n');
  const hit = cache.get(o.id);
  if (hit && hit.key === key) return { text: hit.text, pending: fromModule === undefined };
  const parts: string[] = [];
  if (o.name) parts.push(o.name);
  if (typeof fromModule === 'string') parts.push(fromModule);
  else {
    // what the object draws
    let drawn = 0;
    for (const p of ed.scene.primitives(o)) {
      if (p.k === 'text' && p.text) {
        parts.push(p.text);
        drawn++;
      }
    }
    // text a core object keeps but may not draw right now (e.g. an empty-looking connector label)
    for (const k of ['text', 'label', 'title'] as const) {
      const v = o.props[k];
      if (typeof v === 'string' && v && !parts.includes(v)) parts.push(v);
    }
    // a module that neither draws text nor gives it: its data, minus colors and ids
    if (isModule && !drawn && fromModule === null) propsText(o.props, parts);
    if (o.type === CoreTypes.diagram && !drawn && typeof o.props['source'] === 'string') parts.push(o.props['source'] as string);
  }
  const text = parts.join(' ').replace(/\s+/g, ' ').trim().toLowerCase();
  cache.set(o.id, { key, text });
  if (cache.size > 50000) cache.clear();
  return { text, pending: fromModule === undefined };
}

/** A piece of the object's text around the match, for the search bar. */
export function snippet(text: string, q: string, span = 36): string {
  const i = text.indexOf(q);
  if (i < 0) return '';
  const from = Math.max(0, i - span);
  const to = Math.min(text.length, i + q.length + span);
  return (from > 0 ? '…' : '') + text.slice(from, to) + (to < text.length ? '…' : '');
}

/** The color an object is seen as (fill, else outline, else text), as #rrggbb in lower case; null when it has none. */
export function mainColor(o: ObjectRecord): string | null {
  const f = o.style.fill;
  const c = f.kind !== 'none' ? f.color : o.style.stroke && o.style.stroke.width > 0 ? o.style.stroke.color : o.style.text?.color;
  if (!c) return null;
  let h = c.toLowerCase();
  if (h.length === 4 || h.length === 5) h = '#' + [...h.slice(1, 4)].map((ch) => ch + ch).join('');
  return h.slice(0, 7);
}
